import { useEffect, useState } from 'react';
import { cloudApi } from '../services/api';
import {
  BUSINESS_BRIEF_DOWNLOAD_NAME,
  BUSINESS_BRIEF_FILENAME,
  BUSINESS_BRIEF_TEMPLATE_PATH,
  BUSINESS_CONTEXT_BANNER,
  BUSINESS_CONTEXT_HEADING,
  BUSINESS_CONTEXT_PRIVACY,
  FILE_ROLE_OPTIONS,
  buildCloudStorageMeta,
  connectedStorageStatusLine,
  isFileRoleValue,
  isLiveCloudProvider,
  type CloudStorageMeta,
  type FileRoleValue,
  type Tier2SelectedFile,
} from '../lib/cloudStorage';
import Tier2DocumentPicker from './Tier2DocumentPicker';
import './BusinessContextPanel.css';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(file);
  });
}

function standardUploadName(file: File, role: FileRoleValue): string {
  if (role !== 'business_brief') return file.name;
  const extMatch = file.name.match(/(\.[a-z0-9]+)$/i);
  return `${BUSINESS_BRIEF_FILENAME}${extMatch ? extMatch[1] : '.txt'}`;
}

interface BusinessContextPanelProps {
  scope?: 'user' | 'org';
  cloud: CloudStorageMeta;
}

export default function BusinessContextPanel({
  scope = 'user',
  cloud,
}: BusinessContextPanelProps) {
  const [role, setRole] = useState<FileRoleValue>('business_brief');
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [liveCloud, setLiveCloud] = useState(cloud);
  const [files, setFiles] = useState<Tier2SelectedFile[]>([]);
  const [pickerNonce, setPickerNonce] = useState(0);

  const liveGoogle = isLiveCloudProvider(liveCloud.provider)
    && liveCloud.connected
    && Boolean(liveCloud.folder_id);

  const refresh = async () => {
    try {
      const data = await cloudApi.status(scope);
      setLiveCloud(buildCloudStorageMeta(data.cloud_storage || {}));
    } catch {
      setLiveCloud(cloud);
    }
    try {
      const selected = await cloudApi.getSelectedFiles(scope);
      setFiles((selected.files || []) as Tier2SelectedFile[]);
    } catch {
      setFiles([]);
    }
  };

  useEffect(() => {
    void refresh();
  }, [scope, cloud.connected, cloud.folder_id, cloud.provider]);

  const handleUpload = async (input: HTMLInputElement) => {
    const file = input.files?.[0];
    if (!file) {
      setStatus(
        role === 'business_brief'
          ? 'Choose your completed Business Brief file first.'
          : 'Choose a file to upload.',
      );
      return;
    }
    if (!liveGoogle) {
      setStatus('Connect Google Drive first, then upload into your folder.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setStatus('That file is over 8 MB. Use a smaller document.');
      return;
    }

    const uploadName = standardUploadName(file, role);
    if (role === 'business_brief') {
      const base = file.name.replace(/\.[^.]+$/, '').trim().toLowerCase().replace(/[_-]+/g, ' ');
      if (base && !base.includes('business brief')) {
        const ok = window.confirm(
          `Business briefs should be named “${BUSINESS_BRIEF_FILENAME}”.\n\n`
          + `Your file is “${file.name}”. Upload it as “${uploadName}” in Drive?\n\n`
          + 'OK = upload with the standard name · Cancel = rename the file first',
        );
        if (!ok) return;
      }
    }

    setUploading(true);
    setStatus(`Uploading ${uploadName}…`);
    try {
      const contentBase64 = await fileToBase64(file);
      const data = await cloudApi.uploadWorkingFile({
        scope,
        fileName: uploadName,
        mimeType: file.type,
        role,
        contentBase64,
      });
      input.value = '';
      setFiles((data.files || []) as Tier2SelectedFile[]);
      if (data.cloud_storage) setLiveCloud(buildCloudStorageMeta(data.cloud_storage));
      setPickerNonce((current) => current + 1);
      setStatus(
        `Uploaded “${data.file?.filename || uploadName}” into “${data.folderLabel || liveCloud.folder_label}”. `
        + (role === 'business_brief'
          ? 'Your AI-Mentor can now use this brief in conversation.'
          : 'The file stays in your Google Drive.'),
      );
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="business-context-panel">
      <h2 className="pro-setup-section-heading">{BUSINESS_CONTEXT_HEADING}</h2>
      <p className="pro-setup-systems-note business-context-banner">{BUSINESS_CONTEXT_BANNER}</p>
      <p className="pro-setup-systems-note">{BUSINESS_CONTEXT_PRIVACY}</p>
      <p className="pro-setup-helper-line" role="status">
        {connectedStorageStatusLine(liveCloud)}
      </p>

      <label className="pro-setup-label scannable-bullet-text">
        File role
        <select
          className="input-field pro-setup-input pro-setup-select"
          value={role}
          onChange={(event) => {
            const next = event.target.value;
            if (isFileRoleValue(next)) setRole(next);
          }}
        >
          {FILE_ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {role === 'business_brief' && (
        <p className="pro-setup-helper-line">
          Download the blank template, fill in your business basics, save it as{' '}
          <code>{BUSINESS_BRIEF_DOWNLOAD_NAME}</code>, then upload it into your Drive folder.
        </p>
      )}

      <div className="business-context-actions">
        {role === 'business_brief' && (
          <a
            className="btn-primary pro-setup-upload-btn business-context-download"
            href={BUSINESS_BRIEF_TEMPLATE_PATH}
            download={BUSINESS_BRIEF_DOWNLOAD_NAME}
          >
            Download blank Business Brief template
          </a>
        )}
        <label className="btn-primary pro-setup-upload-btn business-context-upload-label">
          {uploading ? 'Uploading…' : 'Upload to Drive folder'}
          <input
            className="pro-setup-file-input"
            type="file"
            accept=".txt,.md,.csv,.pdf,.doc,.docx,text/plain,application/pdf"
            disabled={uploading}
            onChange={(event) => {
              void handleUpload(event.currentTarget);
            }}
          />
        </label>
      </div>

      <p className="pro-setup-section-subheading">Or choose files already in your Drive folder</p>
      <Tier2DocumentPicker
        key={pickerNonce}
        scope={scope}
        cloud={liveCloud}
        category={role}
        buttonLabel="Select files from Drive"
        onFilesChange={setFiles}
      />
      {status && <p className="pro-setup-helper-line" role="status">{status}</p>}
    </div>
  );
}
